const CONSENT_KEY = 'cookie_consent'

export type ConsentStatus = 'accepted' | 'rejected' | 'pending'

export interface ConsentPreferences {
  analytics: boolean
  timestamp: number
}

/**
 * Get current consent preferences from localStorage
 */
export function getConsentPreferences(): ConsentPreferences | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (!stored) return null
    return JSON.parse(stored) as ConsentPreferences
  } catch {
    return null
  }
}

/**
 * Save consent preferences to localStorage
 */
export function setConsentPreferences(preferences: ConsentPreferences): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences))
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Check if user has made a consent decision
 */
export function hasConsentDecision(): boolean {
  return getConsentPreferences() !== null
}

/**
 * Check if analytics consent was given
 */
export function hasAnalyticsConsent(): boolean {
  const prefs = getConsentPreferences()
  return prefs?.analytics ?? false
}

/**
 * Accept all cookies including analytics
 */
export function acceptAllCookies(): void {
  setConsentPreferences({
    analytics: true,
    timestamp: Date.now(),
  })
}

/**
 * Reject non-essential cookies (only essential allowed)
 */
export function rejectNonEssentialCookies(): void {
  setConsentPreferences({
    analytics: false,
    timestamp: Date.now(),
  })
}

/**
 * Clear consent preferences (for testing or reset)
 */
export function clearConsentPreferences(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(CONSENT_KEY)
}

/**
 * Load Vercel Analytics only if consent was given
 */
export async function loadAnalyticsIfConsented(): Promise<void> {
  if (!hasAnalyticsConsent()) return

  try {
    const { inject } = await import('@vercel/analytics')
    inject()
  } catch {
    // Analytics failed to load - not critical
  }
}
